import { MQTTClient } from '@frontend/core'
import { MqttProtocol } from 'mqtt'
import { useCallback, useEffect, useRef, useState } from 'react'

export interface MQTTConnectionConfig {
  name: string
  locationName: string
  WSSHost: string
  Username: string
  Password: string
}

export const useMQTTClient = (initialConfig: MQTTConnectionConfig | null) => {
  const [isConnected, setIsConnected] = useState(false)
  const [error, setError] = useState<string | null>()
  const isError = !!error && error !== ''
  const [client, setClient] = useState<MQTTClient | null>(null)
  const [config, setConfig] = useState<MQTTConnectionConfig | null>(
    initialConfig
  )
  const clientIdRef = useRef('')

  const onDisconnect = useCallback((clientId: string) => {
    if (clientId !== clientIdRef.current) return
    setIsConnected(false)
  }, [])

  const onError = useCallback((clientId: string, error: Error) => {
    if (clientId !== clientIdRef.current) return
    console.error(error)
    setError(error?.message)
  }, [])

  const onConnection = useCallback((clientId: string) => {
    if (clientId !== clientIdRef.current) return
    setIsConnected(true)
    setError(null)
  }, [])

  useEffect(() => {
    if (!config) return
    const client = connectSportableBroker(
      config,
      onConnection,
      onError,
      onDisconnect
    )

    setClient(client)
    const clientId = client.getClientId()
    clientIdRef.current = clientId ?? ''

    return () => {
      client.endClient()
    }
  }, [config])

  return {
    error,
    isError,
    isConnected,
    client,
    setConfig
  }
}

//Connect to Sportable MQTT Broker
export function connectSportableBroker(
  broker: MQTTConnectionConfig,
  onConnection: (clientId: string) => void,
  onError: (clientId: string, error: Error) => void,
  onClose?: (clientId: string) => void
) {
  const mqttClient = new MQTTClient(broker.name, broker.locationName)

  const uniqueSuffix = Math.floor((1 + Math.random()) * 0x100000000)
    .toString(16)
    .substring(1)

  // Use regex to determine protocol and path of broker connection
  const regex = new RegExp('^wss')
  const isWSS = regex.test(broker.WSSHost)
  const protocol = isWSS ? 'wss' : ('ws' as MqttProtocol)
  const path = isWSS ? '' : '/ws'

  const options = {
    clientId: `match_tracker_ui_${uniqueSuffix}`,
    protocol,
    username: broker.Username,
    password: broker.Password,
    keepalive: 30,
    path
  }

  console.log('Connecting to MQTT broker')

  mqttClient.connect(
    broker.WSSHost,
    options,
    (clientId: string) => {
      // Success
      onConnection(clientId)
    },
    (clientId: string, error: Error) => {
      // Error
      onError(clientId, error)
    },
    (clientId: string) => {
      // onClose
      if (onClose) {
        onClose(clientId)
      } else {
        console.log('no onClose handler for: ', clientId)
      }
    }
  )

  return mqttClient
}
